import React, { useEffect, useRef, useState } from 'react';
import { isEqual, uniq, uniqWith } from 'lodash';

import CardLayout from '@magoosh/layouts/card';
import { FontAwesomeIcon, SparkleIcon } from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import { AnswerData } from 'types/answer';
import fetch from 'utilities/fetch';
import { toSentence } from 'utilities/formatting';

interface Props {
  answers: AnswerData[];
  onChange?: (updatedAnswers: AnswerData[]) => void;
  onFinished?: () => void;
}

const ERROR = 'error';
const FINISHED = 'finished';
const PROCESSING = 'processing';

export default (props: Props) => {
  const poller = useRef(null);
  const [answers, setAnswers] = useState<AnswerData[]>(props.answers);
  const scoreReports = uniqWith(answers.map((answer) => answer.scoreReport), isEqual);

  const status = (reports = scoreReports) => {
    if (scoreReports.every((scoreReport) => scoreReport.status === FINISHED)) {
      return FINISHED;
    } else if (scoreReports.some((scoreReport) => scoreReport.status === ERROR)) {
      return ERROR;
    } else {
      return PROCESSING;
    }
  };

  useEffect(() => {
    setAnswers(props.answers);
  }, [props.answers]);

  useEffect(() => {
    if (props.onChange && !isEqual(answers, props.answers)) props.onChange(answers);

    if (status() === FINISHED) {
      clearInterval(poller.current);
      if (props.onFinished && !isEqual(answers, props.answers)) props.onFinished();
    } else if (status() === ERROR) {
      clearInterval(poller.current);
    } else if (status() === PROCESSING) {
      pollForStatusUpdates();
    }

    return () => clearInterval(poller.current)
  }, [answers]);

  const pollForStatusUpdates = () => {
    if (poller.current) clearInterval(poller.current);

    poller.current = setInterval(() => {
      const ids = scoreReports.map((scoreReport) => scoreReport.id)

      fetch(paths.api.aiGradingRequestsStatus(ids)).then((response) => {
        const updatedDataByAnswerId = {};

        // Look at the grading requests and map to an Object keyed by answer id for fast lookup
        response.gradingRequests.forEach((gradingRequest) => (
          gradingRequest.answers.forEach((answer) => (
            updatedDataByAnswerId[answer.id] = {
              gradingRequestGradeValue: answer.score,
              scoreReport: {
                status: gradingRequest.status
              }
            }
          ))
        ));

        setAnswers((answers) => (
          answers.map((answer) => {
            const updatedData = updatedDataByAnswerId[answer.id];

            return {
              ...answer,
              gradingRequestGradeValue: updatedData.gradingRequestGradeValue,
              scoreReport: { ...answer.scoreReport, ...updatedData.scoreReport },
            };
          })
        ));
      });
    }, 5000);
  };

  const retryGrading = async () => {
    const scoreReportIdsWithErrors = scoreReports.filter((scoreReport) => (
      scoreReport.status === ERROR
    )).map((scoreReport) => scoreReport.id);

    await Promise.all(scoreReportIdsWithErrors.map((id) => (
      fetch(paths.api.aiGradingRequestRegrade(id), { method: 'POST' })
    )));

    setAnswers((answers) => (
      answers.map((answer) => {
        if (scoreReportIdsWithErrors.indexOf(answer.scoreReport.id) === -1) {
          return answer;
        } else {
          return {
            ...answer,
            scoreReport: {
              ...answer.scoreReport,
              status: 'processing'
            }
          };
        }
      })
    ));
  };

  if (status() === PROCESSING) {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <FontAwesomeIcon className="fa-spin" icon="refresh" style={{ color: '#2A6EB6' }} />
          </div>

          <strong>
            We're grading your {toSentence(uniq(scoreReports.map((scoreReport) => scoreReport.requestType)))} answers. This can take a minute or two.
          </strong>
        </div>
      </CardLayout.Card>
    );
  } else if (status() === ERROR) {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <FontAwesomeIcon icon="exclamation-triangle" />
          </div>

          <div>
            <strong>
              We ran into an issue grading your answers.
              {' '}<a onClick={retryGrading} style={{ color: '#2A6EB6' }}>Retry grading</a>
              {' '}and if you are still having trouble, contact us at
              {' '}<a href="mailto:help@magoosh.com" style={{ color: '#2A6EB6' }}>help@magoosh.com</a>.
            </strong>
          </div>
        </div>
      </CardLayout.Card>
    );
  } else {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <SparkleIcon color="#2A6EB6" size={20} />
          </div>

          <strong>
            We've finished grading your {toSentence(uniq(scoreReports.map((scoreReport) => scoreReport.requestType)))} answers!
          </strong>
        </div>
      </CardLayout.Card>
    );
  }
};
